import React from 'react';
import {
  Button,
  ExportButton,
  Filter,
  TextInput,
  SelectInput,
  useListContext,
  usePermissions,
  useTranslate,
} from 'react-admin';
import EditIcon from '@material-ui/icons/Edit';
import { useHistory } from 'react-router-dom';
import { List } from '~/layout';
import {
  DateTimeInput,
  ReferenceInput,
  KycLevelSelect,
  ProfileTagsFilterInput,
  AllPaymentInstrumentsSelect,
} from '~/components/ra';
import { PricingDatagrid } from '../solutions/components/Pricing/PricingDatagrid';
import { useDefaultListFilterStyles } from '~/hooks/useListFilterStyles';
import { rowsPerPageOptionsDefault } from '~/utils/pagination';
import { CustomPagination } from '~/layout/List';
import { makeStyles } from '@material-ui/core/styles';

const PricingFilters = (props: any) => {
  const translate = useTranslate();
  const t = (key: string): string => translate(`resources.solutions.pricings.${key}`);
  const td = (key: string): string => translate(`utils.${key}`);
  const classes = useDefaultListFilterStyles();
  return (
    <Filter {...props} className={classes.form}>
      <TextInput className={classes.formControl} label={t('name')} source='name' alwaysOn />
      <ReferenceInput
        className={classes.formControl}
        source='solutionId'
        reference='solutions'
        allowEmpty
        emptyText={td('any')}
        alwaysOn
      >
        <SelectInput optionText='name' />
      </ReferenceInput>
      <KycLevelSelect
        className={classes.formControl}
        label={t('kycLevel')}
        source='kycLevel'
        alwaysOn
      />
      <DateTimeInput
        className={classes.formControl}
        source='activeFrom'
        label={t('activeFrom')}
        options={{ format: 'LL LT', clearable: true }}
        fullWidth
        alwaysOn
      />
      <AllPaymentInstrumentsSelect
        source='paymentInstrument'
        label={t('paymentInstrument')}
        className={classes.formControl}
        emptyText={td('any')}
        alwaysOn
      />
      <ProfileTagsFilterInput
        label={t('profileTags')}
        className={classes.formControlLong}
        alwaysOn
      />
    </Filter>
  );
};

const ViewDrafts = () => {
  const history = useHistory();
  const translate = useTranslate();

  return (
    <Button
      label={translate('resources.drafts.myDrafts')}
      onClick={() => history.push('/pricings/drafts')}
    >
      <EditIcon />
    </Button>
  );
};

const PricingListActions = () => {
  const { currentSort, filterValues, resource, total } = useListContext();
  const { permissions } = usePermissions();
  const canViewDrafts =
    permissions?.includes('solution.pricing.update') ||
    permissions?.includes('solution.pricing.create');
  const classes = useStyles();

  return (
    <div className={classes.actions}>
      {canViewDrafts && <ViewDrafts />}
      <ExportButton
        disabled={total === 0}
        resource={resource}
        sort={currentSort}
        filterValues={filterValues}
      />
    </div>
  );
};

const PricingList = (props: any) => {
  return (
    <List
      bulkActionButtons={false}
      filters={<PricingFilters />}
      {...props}
      actions={<PricingListActions />}
      sort={{ field: 'name', order: 'ASC' }}
      pagination={
        <CustomPagination rowsPerPageOptions={rowsPerPageOptionsDefault} limit={<span />} />
      }
    >
      <PricingDatagrid rowClick='show' size='medium' />
    </List>
  );
};

const useStyles = makeStyles((theme) => ({
  actions: {
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: 'auto',
    flexShrink: 0,
    '& .MuiButton-root': {
      flexShrink: 0,
      whiteSpace: 'nowrap',
    },
  },
}));

export default PricingList;
